import React from 'react'
import { useTable } from '@refinedev/antd'
import { useUpdate, useCreate } from '@refinedev/core'
import { Switch, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, type Stat } from '../../theme/listkit'
import { Rocket, Target } from '../../components/icons'

interface FeatureFlag {
  key: string
  enabled: boolean
  description: string | null
  updated_at: string
}

/** The three feature-flag keys owned by this curated page (excluded from the generic Feature Flags list). */
export const ONBOARDING_FLAG_KEYS = [
  'onboarding_enabled',
  'onboarding_force_activation',
  'onboarding_force_awareness',
] as const

export const isOnboardingOwned = (key: string): boolean =>
  (ONBOARDING_FLAG_KEYS as readonly string[]).includes(key)

interface CatalogEntry {
  key: string
  title: string
  note: string
  kind: 'master' | 'variant'
}

/**
 * Friendly catalog for the onboarding flags. Surfaced even when the row does not
 * exist yet (a missing row already means "off" to the app) — toggling on CREATES
 * the row. See docs/ONBOARDING_TOUR_PLAN.md.
 */
const ONBOARDING_CATALOG: CatalogEntry[] = [
  {
    key: 'onboarding_enabled',
    title: 'First-run tour (master)',
    note: 'Turns the first-run onboarding on. Off = new members go straight to the app. Keep this on to run the A/B test below.',
    kind: 'master',
  },
  {
    key: 'onboarding_force_activation',
    title: 'Force the Activation flow (A)',
    note: 'Pin every not-yet-onboarded member to the value-first Activation flow. Leave both force toggles off to keep the live 50/50 A/B split.',
    kind: 'variant',
  },
  {
    key: 'onboarding_force_awareness',
    title: 'Force the Feature-Awareness flow (B)',
    note: 'Pin every not-yet-onboarded member to the carousel + tab-tour Feature-Awareness flow. If both force toggles are on, Activation wins.',
    kind: 'variant',
  },
]

/**
 * Onboarding — a curated surface for the first-run tour flags. Unlike the generic
 * Feature Flags list, the three known flags always show here (even before a row
 * exists) so they are discoverable; toggling a not-yet-created flag inserts the
 * row. The mobile app subscribes to feature_flags Realtime, so changes are live.
 */
export function OnboardingSettingsPage() {
  // feature_flags is a small, domain-bounded table; an explicit bounded pageSize
  // (200 >> the real key count) keeps the whole flag set on one page for the
  // onboarding-key lookup below without ever issuing an unbounded fetch.
  const { tableProps } = useTable<FeatureFlag>({
    resource: 'feature_flags',
    syncWithLocation: false,
    pagination: { pageSize: 200 },
    meta: { idColumnName: 'key' },
  })
  const { mutate: update } = useUpdate()
  const { mutate: create } = useCreate()

  const rows = (tableProps.dataSource ?? []) as FeatureFlag[]
  const byKey = new Map(rows.map((r) => [r.key, r]))
  const isLoading = tableProps.loading as boolean

  const items = ONBOARDING_CATALOG.map((catalog) => ({ catalog, row: byKey.get(catalog.key) ?? null }))
  const masterOn = byKey.get('onboarding_enabled')?.enabled ?? false
  const forceA = byKey.get('onboarding_force_activation')?.enabled ?? false
  const forceB = byKey.get('onboarding_force_awareness')?.enabled ?? false
  const variantLabel = forceA ? 'Flow A (forced)' : forceB ? 'Flow B (forced)' : 'A / B · 50-50'

  function setFlag(entry: CatalogEntry, row: FeatureFlag | null, enabled: boolean) {
    if (row) {
      update({ resource: 'feature_flags', id: entry.key, values: { enabled }, meta: { idColumnName: 'key' } })
    } else {
      // First touch — create the row (a missing row already reads as "off").
      create({
        resource: 'feature_flags',
        values: { key: entry.key, enabled, description: entry.note },
        meta: { idColumnName: 'key' },
      })
    }
  }

  const stats: Stat[] = [
    { label: 'First-run tour', value: masterOn ? 'On' : 'Off', icon: Rocket, tone: masterOn ? 'green' : 'neutral' },
    { label: 'Variant', value: variantLabel, icon: Target, tone: 'jaguar' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'System · Feature Flags',
        description:
          'Control the first-run tour new members see. Turn it on with the master switch, then either run the 50/50 A/B test (both force toggles off) or pin everyone to the winning flow. Changes take effect within seconds — no deploy required.',
      }}
      stats={stats}
      statColumns={2}
    >
      <SectionCard title="First-run tour">
        {isLoading ? (
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, padding: '24px 0' }}>
            Loading…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {items.map(({ catalog, row }, idx) => {
              const isMaster = catalog.kind === 'master'
              const enabled = row?.enabled ?? false
              return (
                <div
                  key={catalog.key}
                  data-testid={`onboarding-row-${catalog.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    ...(isMaster
                      ? {
                          background: 'rgba(196,151,49,.06)',
                          borderRadius: 10,
                          padding: '18px 14px',
                          marginBottom: 8,
                          border: '1px solid rgba(196,151,49,.22)',
                        }
                      : {
                          padding: '16px 0',
                          borderBottom: idx < items.length - 1 ? `1px solid ${brand.cardLine}` : 'none',
                        }),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                      {isMaster ? <Rocket size={18} color={brand.gold} /> : <Target size={18} color={brand.jaguar} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: isMaster ? 700 : 600,
                          fontSize: isMaster ? 14 : 13,
                          color: brand.ink,
                          marginBottom: 3,
                        }}
                      >
                        {catalog.title}
                        {!row && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontFamily: brand.fSans,
                              fontWeight: 700,
                              fontSize: 10,
                              color: brand.sub,
                              background: 'rgba(0,0,0,.05)',
                              padding: '2px 7px',
                              borderRadius: 999,
                              verticalAlign: 'middle',
                            }}
                          >
                            NOT SET
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }}>
                        {catalog.note}
                      </div>
                      {row?.updated_at && (
                        <div
                          style={{
                            fontFamily: brand.fSans,
                            fontWeight: 500,
                            fontSize: 11,
                            color: brand.sub,
                            marginTop: 4,
                            opacity: 0.65,
                          }}
                        >
                          Last changed {new Date(row.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <Tooltip title={enabled ? `Disable "${catalog.title}"` : `Enable "${catalog.title}"`}>
                      <Switch
                        checked={enabled}
                        onChange={(checked) => setFlag(catalog, row, checked)}
                        style={isMaster ? { background: enabled ? brand.gold : undefined } : undefined}
                      />
                    </Tooltip>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
